import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

@Pipe({
  name: 'durationMoment',
  pure: true,
  standalone: false,
})
export class DurationMomentPipe implements PipeTransform {
  private translations?: any;

  constructor(private translateService: TranslateService) {}

  transform(start?: Date, end?: Date): Observable<string | null> {
    if (!start || !(start instanceof Date)) return of(null);
    if (end && !(end instanceof Date)) return of(null);

    const endOrToday = end ?? new Date();
    endOrToday.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);

    if (endOrToday < start) return of(null);

    const diffDatesInMilliseconds = Math.abs(
      Math.floor((start.getTime() - endOrToday.getTime()) / 1000),
    );
    const years = endOrToday.getFullYear() - start.getFullYear();
    const months = years * 12 + (endOrToday.getMonth() - start.getMonth());
    const days = Math.floor(diffDatesInMilliseconds / (24 * 60 * 60));

    if (!this.translations) {
      return this.getTranslations().pipe(
        mergeMap((translations) => {
          this.translations = translations;
          return of(this.format(days, months, Math.floor(months / 12)));
        }),
      );
    }
    return of(this.format(days, months, Math.floor(months / 12)));
  }

  private format(days: number, months: number, years: number): string | null {
    const isDay = days >= 0 && days < 30;
    const isMonth = days >= 30 && months <= 11;

    if (isDay) return this.formatDays(days);
    if (isMonth) return this.formatMonth(months, years);
    return this.formatYearAndMonth(months, years);
  }

  private formatYearAndMonth(months: number, years: number): string {
    let textFormatted: string = `${years} ${this.translations[years === 1 ? 'l-year' : 'l-years']}`;

    const month = this.formatMonth(months, years);
    if (month) textFormatted += ` ${this.translations['l-and']} ${month}`;
    return textFormatted;
  }

  private formatMonth(months: number, year: number): string {
    const subtractedMonths = months >= 12 ? months - year * 12 : months;

    if (subtractedMonths > 0)
      return `${subtractedMonths} ${
        this.translations[subtractedMonths === 1 ? 'l-month' : 'l-months']
      }`;
    return '';
  }

  private formatDays(days: number): string {
    return `${days} ${this.translations[days === 1 ? 'l-day' : 'l-days']}`;
  }

  private getTranslations(): Observable<any> {
    return this.translateService.stream([
      'l-year',
      'l-years',
      'l-month',
      'l-months',
      'l-day',
      'l-days',
      'l-and',
    ]);
  }
}
